/* eslint-disable import/no-absolute-path */
import React, { useState } from 'react';
import {
  UserOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  UnlockOutlined,
  ProfileOutlined,
  BellOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  LogoutOutlined,
  Layout,
  Menu,
  useNavigate,
  useAppDispatch,
  eventBus,
  MenuPermissionsGate,
  useTranslation,
  documentAlertService,
  humps,
  SideBarNotification,
  SolutionOutlined,
  useAppSelector,
  jobService,
  MenuWithoutPermission,
  usePermissions,
  ShopOutlined,
  BankOutlined,
  CarOutlined,
  IdcardOutlined,
} from '@lib/index';
import Logo from '/images/logo.png';
import LogoTwo from '/images/logo2.png';
import { ROLES, SCOPES } from '@config/permissionsMap';
import { logOut } from '@features/account/AccountSlice';
import { useMsal } from '@azure/msal-react';
import { useAuth0 } from '@auth0/auth0-react';
import { BlockOutlined, BarChartOutlined } from '@ant-design/icons';
import { SideBarProps } from './types';

const SideBar = ({ triggered, collapsed, className = '' }: SideBarProps) => {
  const EXTERNAL_LOGIN_TYPE = ['auth0', 'entra'];
  const { logout: auth0Logout } = useAuth0();
  const { Sider } = Layout;
  const { t } = useTranslation();
  const { currentUser, impersonatingUser, loginType } = useAppSelector((state) => state.account);
  const isAdmin = currentUser.role === ROLES.admin;
  const { instance, accounts } = useMsal();
  const { role } = currentUser;
  const isStaffingContractor = role === ROLES.staffingContractor;
  const isRegularContractor = role === ROLES.regularContractor;
  const canApproveJobs = currentUser.permissions.includes('can-approve-jobs');
  const showRequestsSidebarBadge = canApproveJobs || isRegularContractor || isStaffingContractor;
  const { useGetPendingAlertsQuery } = documentAlertService;
  const skip = !['staffing_contractor', 'area_section'].includes(role);
  const { data } = useGetPendingAlertsQuery(undefined, { skip });
  const pendingAlerts = humps(data?.count);
  const [sideBarVisible, setSideBarVisible] = useState<boolean>(
    window.innerWidth >= 576,
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const sideBarHiddenClassName = !collapsed
    ? 'fixed top-0 left-0 h-full z-10 overflow-auto'
    : 'hidden';
  const sideBarClassName = sideBarVisible ? className : sideBarHiddenClassName;
  const LogoImg = !collapsed ? Logo : LogoTwo;
  const handleDashboard = () => navigate('/dashboard');
  const handleWorkers = () => navigate('/workers');
  const handleHelpCenter = () => navigate('/help-center');
  const handleWorkersAccesses = () => navigate('/workers-accesses?page=0&tab=accesses');
  const handleAccounts = () => navigate('/accounts');
  const handlePersonnel = () => navigate('/personnel');
  const handleLogout = () => {
    if (impersonatingUser?.email) {
      navigate('/switching/1/remove-impersonation');
      return;
    }

    if (EXTERNAL_LOGIN_TYPE[0] === loginType) {
      auth0Logout();
    }

    if (EXTERNAL_LOGIN_TYPE[1] === loginType) {
      instance.logoutRedirect({
        account: accounts[0],
      });
    }

    eventBus.dispatch('logout', {});
    dispatch(logOut());
    navigate('/');
  };
  const handleJobList = () => navigate('/jobs?page=0');
  const handleDocumentations = () => navigate('/documentations?tab=general');
  const handleAlerts = () => navigate('/alerts');
  const handleSettings = () => navigate('/settings');
  const handleUsers = () => navigate('/users?page=0');
  const handleSiteWorkers = () => navigate('/site-workers');
  const handleContractor = () => navigate('/contractors');
  const handleReporting = () => navigate('/reporting');
  const handleStore = () => navigate('/stores');
  const handleArea = () => navigate('/domain/areas');
  const handleSubContractor = () => navigate('/domain/sub-contractors');

  const handleF30 = () => navigate('/f30');
  const {
    canViewRegularContractorF30Page,
  } = usePermissions();
  const handleVehicles = () => navigate('/vehicles?tab=vehicles');

  const { useGetJobsCountQuery } = jobService;
  const { data: totalJobs } = useGetJobsCountQuery({
    status: 'user_pending',
    scheduled: 'not_finished',
  }, { skip: isAdmin });

  return (
    <Sider
      width="250px"
      className={sideBarClassName}
      trigger={null}
      collapsible
      collapsed={triggered}
      breakpoint="sm"
      onBreakpoint={(broken: boolean) => {
        if (broken) return setSideBarVisible(false);
        return setSideBarVisible(true);
      }}
    >
      <img className="logo h-8 pl-8 my-8" src={LogoImg} alt="Logo" />
      <Menu theme="dark" mode="inline" defaultSelectedKeys={['1']} inlineIndent={35}>
        {currentUser.role !== ROLES.administratorSection && (
        <Menu.Item key="1" icon={<AppstoreOutlined />} onClick={handleDashboard}>
          {t('sidebar.dashboard')}
        </Menu.Item>
        )}
        <MenuPermissionsGate
          scopes={[SCOPES.canViewJobsPage]}
          key="2"
          icon={<FileTextOutlined />}
          handleLink={handleJobList}
          title={t('sidebar.request')}
          indicator={showRequestsSidebarBadge && (
            <SideBarNotification count={totalJobs?.count as number} />
          )}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewContractorsPage]}
          key="3"
          icon={<SolutionOutlined />}
          handleLink={handleContractor}
          title={t('sidebar.contractors')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewReportingPage]}
          key="21"
          icon={<BarChartOutlined />}
          handleLink={handleReporting}
          title={t('sidebar.reporting')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewAreaSectionAreaPage]}
          key="17"
          icon={<BankOutlined />}
          handleLink={handleArea}
          title={t('sidebar.areas')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewAreaSectionContratistasSubcontratoPage]}
          key="19"
          icon={<BlockOutlined />}
          handleLink={handleSubContractor}
          title={t('sidebar.subContractor')}
        />

        {isStaffingContractor && (
        <MenuPermissionsGate
          scopes={[SCOPES.canViewDocumentationsPage]}
          key="4"
          icon={<ProfileOutlined />}
          handleLink={handleDocumentations}
          title={t('sidebar.documentation')}
        />
        )}
        {canViewRegularContractorF30Page && (
          <MenuWithoutPermission
            key="5"
            icon={<ProfileOutlined />}
            handleLink={handleF30}
            title={t('sidebar.documentation')}
          />
        )}

        <MenuPermissionsGate
          scopes={[SCOPES.canViewAreaSectionTiendaPage]}
          key="16"
          icon={<ShopOutlined />}
          handleLink={handleStore}
          title={t('sidebar.stores')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewWorkersPage]}
          key="6"
          icon={<TeamOutlined />}
          handleLink={handleWorkers}
          title={t('sidebar.workers')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canCreateWorkerAnnotations]}
          key="7"
          icon={<TeamOutlined />}
          handleLink={handleSiteWorkers}
          title={t('sidebar.siteWorkers')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewCorpSiteWorkersListPage]}
          key="20"
          icon={<TeamOutlined />}
          handleLink={handleWorkers}
          title={t('sidebar.corpSiteWorkers')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canManagePersonnel]}
          key="8"
          icon={<IdcardOutlined />}
          handleLink={handlePersonnel}
          title={t('sidebar.personnel')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewAreaSectionVehiclesPage]}
          key="9"
          icon={<CarOutlined />}
          handleLink={handleVehicles}
          title={t('sidebar.vehicles')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewAlertsPage]}
          key="10"
          icon={<BellOutlined />}
          handleLink={handleAlerts}
          title={t('sidebar.alerts')}
          indicator={(<SideBarNotification count={pendingAlerts} />)}
          data-cy="alert"
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewWorkerAccessesPage]}
          key="11"
          icon={<UnlockOutlined />}
          handleLink={handleWorkersAccesses}
          title={t('sidebar.workerAccesses')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewUsersPage]}
          key="12"
          icon={<UserOutlined />}
          handleLink={handleUsers}
          title={t('sidebar.users')}
        />
        <MenuPermissionsGate
          scopes={[SCOPES.canViewSettingsPage]}
          key="13"
          icon={<SettingOutlined />}
          handleLink={handleSettings}
          title={t('sidebar.settings')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewHelpCentersPage]}
          key="14"
          icon={<QuestionCircleOutlined />}
          handleLink={handleHelpCenter}
          title={t('sidebar.helpCenter')}
        />

        <MenuPermissionsGate
          scopes={[SCOPES.canViewAccountPage]}
          key="15"
          icon={<SettingOutlined />}
          handleLink={handleAccounts}
          title={t('sidebar.accounts')}
        />

        <Menu.Item
          className="mt-72"
          key="18"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
        >
          {t('sidebar.logout')}
        </Menu.Item>
      </Menu>
    </Sider>
  );
};

export default SideBar;
